import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Plus,
  MessageCircle,
  ThumbsUp,
  Pin,
  CheckCircle2,
  ChevronDown,
  MessageSquare,
  X,
  Users,
  ArrowUpRight,
  Send,
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { getAuthHeader } from '../../services/otpAuthService';
import NewDiscussionModal from './components/NewDiscussionModal';
import SearchBar from './components/SearchBar';
import ReactionBar from './components/ReactionBar';
import { useCommunitySocket } from './hooks/useCommunitySocket';

const API_BASE_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/graphql', '')
  : 'http://localhost:5000';

const getIcon = (name: string) => {
  const Icon = (LucideIcons as any)[name];
  return Icon || MessageSquare;
};

const getAttUrl = (url: string) =>
  url.startsWith('http') ? url : `${process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/graphql', '') : 'http://localhost:5000'}${url}`;

const timeAgo = (date: string) => {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return 'adesso';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min fa`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ore fa`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} giorni fa`;
  return new Date(date).toLocaleDateString('it-IT');
};

interface Area {
  _id: string;
  slug: string;
  displayName: string;
  description: string;
  icon: string;
  color: string;
  type?: string;
  allowAcceptedReplies?: boolean;
  stats: {
    openDiscussions: number;
    participants: number;
  };
}

interface InlineReply {
  _id: string;
  content: string;
  author: { _id?: string; businessName: string; foto?: string; communityProfile?: { avatarColor?: string } } | null;
  isAdminReply?: boolean;
  createdAt: string;
}

interface Discussion {
  _id: string;
  title: string;
  content: string;
  author: {
    businessName: string;
    foto?: string;
    communityProfile?: {
      avatarColor?: string;
    };
  } | null;
  status: 'open' | 'resolved' | 'locked';
  isPinned: boolean;
  tags: string[];
  attachments?: Array<{
    filename?: string;
    originalName?: string;
    url: string;
    mimetype?: string;
    mimeType?: string;
    type?: string;
  }>;
  repliesCount: number;
  reactions: { type: string; user: string }[];
  lastActivityAt: string;
  createdAt: string;
  lastReplies?: InlineReply[];
}

type Tab = 'all' | 'open' | 'resolved' | 'pinned';
type Sort = 'lastActivity' | 'newest' | 'mostReplies';

const SORT_OPTIONS: { value: Sort; label: string }[] = [
  { value: 'lastActivity', label: 'Ultima attivita' },
  { value: 'newest', label: 'Piu recenti' },
  { value: 'mostReplies', label: 'Piu risposte' },
];

const AreaPage: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const toast = useToast();

  const [areas, setAreas] = useState<Area[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [currentArea, setCurrentArea] = useState<Area | null>(null);
  const [loadingArea, setLoadingArea] = useState(true);
  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [loadingDiscussions, setLoadingDiscussions] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('all');
  const [sort, setSort] = useState<Sort>('lastActivity');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [currentMemberId, setCurrentMemberId] = useState('');

  // Real-time lounge socket
  const onNewReply = useCallback((discussionId: string, reply: InlineReply) => {
    setDiscussions(prev => prev.map(d => {
      if (d._id !== discussionId) return d;
      if ((d.lastReplies || []).some(r => r._id === reply._id)) return d;
      return { ...d, lastReplies: [...(d.lastReplies || []), reply], repliesCount: d.repliesCount + 1 };
    }));
  }, []);

  const onReactionUpdate = useCallback((discussionId: string, reactions: { type: string; user: string }[]) => {
    setDiscussions(prev => prev.map(d =>
      d._id === discussionId ? { ...d, reactions } : d
    ));
  }, []);

  useCommunitySocket(
    currentArea?.type === 'lounge' ? slug : undefined,
    onNewReply,
    onReactionUpdate
  );

  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/community/v2/areas`, {
          headers: getAuthHeader() as any,
        });
        if (res.ok) {
          const result = await res.json();
          setAreas(result.data || []);
        }
      } catch (err) {
        console.error('Error fetching areas:', err);
      }
    };
    fetchAreas();
  }, []);

  useEffect(() => {
    if (!slug) return;
    const found = areas.find((a) => a.slug === slug);
    if (found) {
      setCurrentArea(found);
      setLoadingArea(false);
    } else if (areas.length > 0) {
      setCurrentArea(null);
      setLoadingArea(false);
    }
  }, [slug, areas]);

  const fetchDiscussions = useCallback(
    async (cursor?: string) => {
      if (!slug) return;
      if (!cursor) setLoadingDiscussions(true);
      else setLoadingMore(true);

      try {
        const params = new URLSearchParams({ tab: activeTab, sort, limit: '20' });
        if (cursor) params.set('before', cursor);

        const res = await fetch(
          `${API_BASE_URL}/api/community/v2/areas/${slug}/discussions?${params}`,
          { headers: getAuthHeader() as any }
        );

        if (res.ok) {
          const result = await res.json();
          if (cursor) {
            setDiscussions((prev) => [...prev, ...(result.data || [])]);
          } else {
            setDiscussions(result.data || []);
          }
          setHasMore(result.hasMore || false);
          setNextCursor(result.nextCursor || null);
          if (result.currentMemberId) setCurrentMemberId(result.currentMemberId);
        } else {
          toast.error('Errore nel caricamento delle discussioni');
        }
      } catch (err) {
        console.error('Error fetching discussions:', err);
        toast.error('Errore di connessione');
      } finally {
        setLoadingDiscussions(false);
        setLoadingMore(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
    [slug, activeTab, sort]
  );

  useEffect(() => {
    setDiscussions([]);
    setNextCursor(null);
    fetchDiscussions();
  }, [fetchDiscussions]);

  useEffect(() => {
    if (!sortDropdownOpen) return;
    const handler = () => setSortDropdownOpen(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [sortDropdownOpen]);

  if (loadingArea && areas.length === 0) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] dark:bg-gray-950 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-indigo-200 dark:border-indigo-800 border-t-indigo-600 dark:border-t-indigo-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (!loadingArea && !currentArea) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] dark:bg-gray-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-8 text-center">
          <h1 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Area non trovata</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            L'area "{slug}" non esiste o non e accessibile.
          </p>
          <button
            onClick={() => navigate('/area-riservata/community-v2')}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-xl transition-colors text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna alla Community
          </button>
        </div>
      </div>
    );
  }

  const AreaIcon = currentArea ? getIcon(currentArea.icon) : MessageSquare;

  const areaAllowsAccept = currentArea?.allowAcceptedReplies !== false;

  const tabs: { key: Tab; label: string }[] = areaAllowsAccept
    ? [
        { key: 'all', label: 'Tutte' },
        { key: 'open', label: 'Aperte' },
        { key: 'resolved', label: 'Risolte' },
        { key: 'pinned', label: 'Pinnate' },
      ]
    : [
        { key: 'all', label: 'Tutte' },
        { key: 'pinned', label: 'Pinnate' },
      ];

  return (
    <div className="min-h-screen bg-[#f8f9fb] dark:bg-gray-950">
      {/* Top bar */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 h-13 flex items-center gap-2.5">
          <button
            onClick={() => navigate('/area-riservata/community-v2')}
            className="p-2 -ml-1 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl transition-colors"
          >
            <ArrowLeft className="w-4.5 h-4.5 text-gray-500 dark:text-gray-400" />
          </button>
          <Link
            to="/area-riservata/community-v2"
            className="text-sm text-gray-400 dark:text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
          >
            Community
          </Link>
          <ChevronRight className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {currentArea?.displayName}
          </span>

          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="ml-auto lg:hidden p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl transition-colors"
          >
            <MessageSquare className="w-4.5 h-4.5 text-gray-500" />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto flex">
        {/* Mobile sidebar overlay */}
        {mobileSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setMobileSidebarOpen(false)}
          >
            <div
              className="absolute left-0 top-0 bottom-0 w-72 bg-white dark:bg-gray-900 shadow-2xl overflow-y-auto border-r border-gray-100 dark:border-gray-800"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800">
                <span className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Aree</span>
                <button onClick={() => setMobileSidebarOpen(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              </div>
              <SidebarContent
                areas={areas}
                activeSlug={slug || ''}
                onNavigate={(s) => {
                  setMobileSidebarOpen(false);
                  navigate(`/area-riservata/community-v2/area/${s}`);
                }}
              />
            </div>
          </div>
        )}

        {/* Desktop sidebar */}
        <div
          className={`hidden lg:block flex-shrink-0 transition-all duration-200 ${
            sidebarOpen ? 'w-60' : 'w-11'
          }`}
        >
          <div className="sticky top-13 h-[calc(100vh-3.25rem)] border-r border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-y-auto">
            <div className="flex items-center justify-end p-2">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-1.5 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                {sidebarOpen ? (
                  <ChevronLeft className="w-3.5 h-3.5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                )}
              </button>
            </div>
            <SidebarContent
              areas={areas}
              activeSlug={slug || ''}
              onNavigate={(s) => navigate(`/area-riservata/community-v2/area/${s}`)}
              collapsed={!sidebarOpen}
            />
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0 px-4 py-5 lg:px-8">
          {/* Area header */}
          {currentArea && (
            <div className="relative bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden mb-5">
              {/* Decorative gradient bar */}
              <div className="h-1" style={{ background: `linear-gradient(90deg, ${currentArea.color}, ${currentArea.color}88, transparent)` }} />
              <div className="p-5">
                <div className="flex items-start gap-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
                    style={{ backgroundColor: `${currentArea.color}15`, border: `1.5px solid ${currentArea.color}25` }}
                  >
                    <AreaIcon className="w-5.5 h-5.5" style={{ color: currentArea.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h1 className="text-base font-bold text-gray-900 dark:text-white tracking-tight">
                      {currentArea.displayName}
                    </h1>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed line-clamp-2">
                      {currentArea.description}
                    </p>
                    <div className="flex items-center gap-4 mt-3">
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-2.5 py-1 rounded-lg">
                        <MessageSquare className="w-3 h-3" />
                        {currentArea.stats.openDiscussions} discussioni
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-2.5 py-1 rounded-lg">
                        <Users className="w-3 h-3" />
                        {currentArea.stats.participants} partecipanti
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowNewModal(true)}
                    className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl transition-all text-sm font-medium flex-shrink-0 shadow-sm shadow-indigo-600/20 dark:shadow-none hover:shadow-md hover:shadow-indigo-600/25"
                  >
                    <Plus className="w-4 h-4" />
                    {currentArea?.type === 'lounge' ? 'Nuovo Post' : 'Nuova Discussione'}
                  </button>
                </div>
                <button
                  onClick={() => setShowNewModal(true)}
                  className="sm:hidden mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl transition-colors text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Nuova Discussione
                </button>
              </div>
            </div>
          )}

          {/* Tabs + Sort */}
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div className="flex gap-0.5 bg-gray-100 dark:bg-gray-800/80 rounded-xl p-1">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`px-4 py-1.5 text-[13px] font-medium rounded-lg transition-all ${
                    activeTab === t.key
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <SearchBar className="hidden md:block" />

            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSortDropdownOpen(!sortDropdownOpen);
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-[13px] text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
              >
                {SORT_OPTIONS.find((o) => o.value === sort)?.label}
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {sortDropdownOpen && (
                <div className="absolute right-0 mt-1.5 w-48 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-10 overflow-hidden">
                  {SORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setSort(opt.value);
                        setSortDropdownOpen(false);
                      }}
                      className={`block w-full text-left px-4 py-2.5 text-[13px] transition-colors ${
                        sort === opt.value
                          ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 font-medium'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Discussions list */}
          {loadingDiscussions ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-2 border-indigo-200 dark:border-indigo-800 border-t-indigo-600 dark:border-t-indigo-400 rounded-full animate-spin" />
            </div>
          ) : discussions.length === 0 ? (
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-12 text-center">
              <div className="w-14 h-14 bg-gray-50 dark:bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-7 h-7 text-gray-300 dark:text-gray-600" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1.5">
                Nessuna discussione
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-5 max-w-sm mx-auto leading-relaxed">
                {activeTab === 'all' || activeTab === 'open'
                  ? 'Non ci sono ancora discussioni in questa area. Inizia tu la prima!'
                  : activeTab === 'resolved'
                  ? 'Non ci sono discussioni risolte in questa area.'
                  : 'Non ci sono discussioni pinnate in questa area.'}
              </p>
              {(activeTab === 'all' || activeTab === 'open') && (
                <button
                  onClick={() => setShowNewModal(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-colors text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Crea la prima discussione
                </button>
              )}
            </div>
          ) : (
            <div className={currentArea?.type === 'lounge' ? 'columns-1 md:columns-2 gap-4 [&>*]:mb-4 [&>*]:break-inside-avoid' : 'space-y-2'}>
              {discussions.map((d) => (
                currentArea?.type === 'lounge'
                  ? <LoungeFeedCard key={d._id} discussion={d} currentMemberId={currentMemberId} />
                  : <DiscussionCard key={d._id} discussion={d} />
              ))}

              {hasMore && (
                <div className="text-center pt-4">
                  <button
                    onClick={() => nextCursor && fetchDiscussions(nextCursor)}
                    disabled={loadingMore}
                    className="px-6 py-2 text-[13px] font-medium text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-900 border border-indigo-200 dark:border-indigo-800 rounded-xl hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors disabled:opacity-50"
                  >
                    {loadingMore ? 'Caricamento...' : 'Carica altre'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <NewDiscussionModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        areaSlug={slug || ''}
        areaName={currentArea?.displayName || ''}
        areaType={currentArea?.type}
        onCreated={(newDiscussion) => {
          if (newDiscussion?.data || newDiscussion) {
            const d = newDiscussion.data || newDiscussion;
            setDiscussions(prev => [d, ...prev]);
          }
          setShowNewModal(false);
        }}
      />
    </div>
  );
};

/* --- Sidebar Content --- */
interface SidebarContentProps {
  areas: Area[];
  activeSlug: string;
  onNavigate: (slug: string) => void;
}

const SidebarContent: React.FC<SidebarContentProps & { collapsed?: boolean }> = ({ areas, activeSlug, onNavigate, collapsed }) => (
  <div className={collapsed ? 'py-1 flex flex-col items-center gap-0.5' : 'py-1.5'}>
    {areas.map((area) => {
      const isActive = area.slug === activeSlug;
      const AreaIcon = getIcon(area.icon);
      if (collapsed) {
        return (
          <button
            key={area._id}
            onClick={() => onNavigate(area.slug)}
            title={area.displayName}
            className={`w-9 h-9 flex items-center justify-center rounded-xl transition-all ${
              isActive
                ? 'bg-indigo-50 dark:bg-indigo-950/30 ring-1 ring-indigo-200 dark:ring-indigo-800'
                : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
            }`}
          >
            <AreaIcon className="w-4.5 h-4.5" style={{ color: isActive ? area.color : undefined }} />
          </button>
        );
      }
      return (
        <button
          key={area._id}
          onClick={() => onNavigate(area.slug)}
          className={`w-full flex items-center gap-2.5 px-4 py-2 text-left transition-all ${
            isActive
              ? 'bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 border-r-2 border-indigo-500'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50'
          }`}
        >
          <span
            className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${area.color}15` }}
          >
            <AreaIcon className="w-3.5 h-3.5" style={{ color: area.color }} />
          </span>
          <span className="text-[13px] truncate flex-1">{area.displayName}</span>
          <span className={`text-[11px] flex-shrink-0 tabular-nums ${isActive ? 'text-indigo-500 dark:text-indigo-400 font-semibold' : 'text-gray-400 dark:text-gray-600'}`}>
            {area.stats.openDiscussions}
          </span>
        </button>
      );
    })}
  </div>
);

/* --- Discussion Card --- */
interface DiscussionCardProps {
  discussion: Discussion;
}

const DiscussionCard: React.FC<DiscussionCardProps> = ({ discussion: d }) => {
  const authorName = d.author?.businessName || 'UNASCA';
  const initials = authorName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'UN';

  const avatarColor = d.author?.communityProfile?.avatarColor || '#6366f1';

  return (
    <Link
      to={`/area-riservata/community-v2/discussione/${d._id}`}
      className={`block bg-white dark:bg-gray-900 rounded-xl border transition-all duration-200 group hover:shadow-sm ${
        d.isPinned
          ? 'border-amber-200/80 dark:border-amber-800/40 hover:border-amber-300 dark:hover:border-amber-700'
          : 'border-gray-100 dark:border-gray-800 hover:border-gray-200 dark:hover:border-gray-700'
      }`}
    >
      {d.isPinned && (
        <div className="h-0.5 bg-gradient-to-r from-amber-400 via-amber-300 to-transparent rounded-t-xl" />
      )}
      <div className="flex gap-3.5 p-4">
        {/* Avatar */}
        <div className="flex-shrink-0 relative">
          {d.author?.foto ? (
            <img src={getAttUrl(d.author.foto)} alt={authorName} className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <span
              className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
              style={{ backgroundColor: avatarColor }}
            >
              {initials}
            </span>
          )}
          {/* Status dot */}
          {d.status === 'resolved' ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-500 absolute -bottom-0.5 -right-0.5 bg-white dark:bg-gray-900 rounded-full" />
          ) : d.status === 'locked' ? (
            <div className="w-4 h-4 bg-gray-400 text-white absolute -bottom-0.5 -right-0.5 rounded-full flex items-center justify-center" style={{ fontSize: 8 }}>🔒</div>
          ) : d.isPinned ? (
            <Pin className="w-3.5 h-3.5 text-amber-500 absolute -bottom-0.5 -right-0.5 bg-white dark:bg-gray-900 rounded-full" />
          ) : null}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-1 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                  {d.title}
                </h3>
                {d.status === 'resolved' && (
                  <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
                    Risolta
                  </span>
                )}
                {d.status === 'locked' && (
                  <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                    Bloccata
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 block">
                {authorName} &middot; {timeAgo(d.lastActivityAt)}
              </span>
            </div>
            <ArrowUpRight className="w-3.5 h-3.5 text-gray-200 dark:text-gray-700 group-hover:text-indigo-400 transition-all flex-shrink-0 mt-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </div>

          {d.tags.length > 0 && (
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {d.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 ring-1 ring-gray-100 dark:ring-gray-700"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3.5 mt-2 text-[11px] text-gray-400 dark:text-gray-500">
            <span className="flex items-center gap-1">
              <MessageCircle className="w-3 h-3" />
              {d.repliesCount}
            </span>
            {d.reactions.length > 0 && (
              <span className="flex items-center gap-1">
                <ThumbsUp className="w-3 h-3" />
                {d.reactions.length}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

/* --- Lounge Feed Card (Facebook-style interactive) --- */

const EMOJI_MAP: Record<string, string> = {
  useful: '\uD83D\uDC4D',
  thanks: '\uD83D\uDE4F',
  interesting: '\uD83D\uDCA1',
  celebrate: '\uD83C\uDF89',
  laugh: '\uD83D\uDE02',
  love: '\u2764\uFE0F',
  fire: '\uD83D\uDD25',
  wow: '\uD83D\uDE2E',
};

/* Inline reply bubble */
const InlineReplyBubble: React.FC<{ reply: InlineReply }> = ({ reply: r }) => {
  const name = r.author?.businessName || 'UNASCA Staff';
  const initials = name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'UN';
  const color = r.author?.communityProfile?.avatarColor || '#6366f1';
  const isStaff = !r.author || r.isAdminReply;
  const text = (r.content || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/\s+/g, ' ').trim();

  return (
    <div className="flex items-start gap-2">
      {r.author?.foto ? (
        <img src={getAttUrl(r.author.foto)} alt={name} className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-0.5" />
      ) : (
        <span className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-white text-[9px] font-bold mt-0.5" style={{ backgroundColor: color }}>
          {initials}
        </span>
      )}
      <div className="flex-1 min-w-0">
        <div className="inline-block bg-gray-100 dark:bg-gray-800 rounded-2xl px-3 py-1.5 max-w-full">
          <span className={`text-[12px] font-bold ${isStaff ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-900 dark:text-white'}`}>
            {name}
          </span>
          <p className="text-[13px] text-gray-700 dark:text-gray-300 leading-snug break-words">{text}</p>
        </div>
        <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-3">{timeAgo(r.createdAt)}</span>
      </div>
    </div>
  );
};

interface LoungeFeedCardProps {
  discussion: Discussion;
  currentMemberId: string;
}

const LoungeFeedCard: React.FC<LoungeFeedCardProps> = ({ discussion: d, currentMemberId }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const authorName = d.author?.businessName || 'UNASCA Staff';
  const initials = authorName.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) || 'UN';
  const avatarColor = d.author?.communityProfile?.avatarColor || '#6366f1';
  const isStaff = !d.author;

  // Local state
  const [reactions, setReactions] = useState(d.reactions);
  const [replies, setReplies] = useState<InlineReply[]>(d.lastReplies || []);
  const [repliesCount, setRepliesCount] = useState(d.repliesCount);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Sync from parent (socket updates)
  useEffect(() => { setReactions(d.reactions); }, [d.reactions]);
  useEffect(() => { setReplies(d.lastReplies || []); setRepliesCount(d.repliesCount); }, [d.lastReplies, d.repliesCount]);

  // Strip HTML per preview
  const preview = (d.content || d.title || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 300);

  // Aggregate reaction emojis
  const reactionCounts: Record<string, number> = {};
  reactions.forEach(r => { reactionCounts[r.type] = (reactionCounts[r.type] || 0) + 1; });
  const topReactions = Object.entries(reactionCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);
  const totalReactions = reactions.length;

  // Image attachments
  const images = (d.attachments || []).filter(att => {
    const mime = att.mimetype || att.mimeType || '';
    return att.type === 'image' || mime.startsWith('image/');
  });
  const imageCount = images.length;

  const handleReact = async (type: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/community/v2/discussions/${d._id}/react`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() as any },
        body: JSON.stringify({ type }),
      });
      if (res.ok) {
        const result = await res.json();
        if (result.data?.reactions) setReactions(result.data.reactions);
      }
    } catch { /* silent */ }
  };

  const handleComment = async () => {
    const text = commentText.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/community/v2/discussions/${d._id}/replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() as any },
        body: JSON.stringify({ content: text }),
      });
      if (res.ok) {
        const result = await res.json();
        const newReply = result.data;
        if (newReply) {
          setReplies(prev => {
            if (prev.some(r => r._id === newReply._id)) return prev;
            setRepliesCount(c => c + 1);
            return [...prev, {
              _id: newReply._id,
              content: newReply.content,
              author: newReply.author || null,
              isAdminReply: newReply.isAdminReply,
              createdAt: newReply.createdAt,
            }];
          });
        }
        setCommentText('');
        toast.success('Commento pubblicato');
      } else {
        toast.error('Errore nell\'invio del commento');
      }
    } catch {
      toast.error('Errore di connessione');
    } finally {
      setSubmitting(false);
    }
  };

  const goToThread = () => navigate(`/area-riservata/community-v2/discussione/${d._id}`);

  return (
    <div className="group">
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-[0_1px_2px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none border border-gray-200/60 dark:border-gray-800 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] hover:border-gray-200 dark:hover:border-gray-700 transition-all duration-200 overflow-hidden">
        {/* Header: avatar + name + time */}
        <div className="flex items-center gap-3 px-4 pt-3.5 pb-2">
          {isStaff ? (
            <span className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold bg-gradient-to-br from-indigo-500 to-indigo-700 ring-2 ring-white dark:ring-gray-900">UN</span>
          ) : d.author?.foto ? (
            <img src={getAttUrl(d.author.foto)} alt={authorName} className="w-10 h-10 rounded-full object-cover flex-shrink-0 ring-2 ring-white dark:ring-gray-900" />
          ) : (
            <span className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold ring-2 ring-white dark:ring-gray-900" style={{ backgroundColor: avatarColor }}>
              {initials}
            </span>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`text-[13px] font-bold leading-tight ${isStaff ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-900 dark:text-white'}`}>
                {authorName}
              </span>
              {isStaff && <span className="text-[9px] font-bold px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-800/40 text-indigo-600 dark:text-indigo-300 rounded-full leading-tight">STAFF</span>}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-[11px] text-gray-400 dark:text-gray-500">{timeAgo(d.lastActivityAt)}</span>
              {d.isPinned && <Pin className="w-3 h-3 text-amber-400" />}
            </div>
          </div>
        </div>

        {/* Content - clickable to thread */}
        <div className="px-4 pb-2.5 cursor-pointer" onClick={goToThread}>
          <p className="text-[14px] text-gray-700 dark:text-gray-300 leading-[1.6] line-clamp-5 whitespace-pre-line">
            {preview}{preview.length >= 300 ? '...' : ''}
          </p>
        </div>

        {/* Image attachments gallery */}
        {imageCount > 0 && (
          <div className={`mx-0 cursor-pointer ${imageCount === 1 ? '' : 'grid gap-0.5'} ${imageCount === 2 ? 'grid-cols-2' : imageCount === 3 ? 'grid-cols-3' : imageCount >= 4 ? 'grid-cols-2' : ''}`} onClick={goToThread}>
            {images.slice(0, 4).map((img, i) => (
              <div key={i} className={`relative overflow-hidden ${imageCount === 1 ? 'max-h-[320px]' : 'aspect-square'} bg-gray-100 dark:bg-gray-800`}>
                <img
                  src={getAttUrl(img.url)}
                  alt={img.originalName || img.filename || ''}
                  className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                />
                {i === 3 && imageCount > 4 && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="text-white text-xl font-bold">+{imageCount - 4}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Reactions summary */}
        {(totalReactions > 0 || repliesCount > 0) && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 dark:border-gray-800/60">
            {totalReactions > 0 ? (
              <div className="flex items-center gap-1.5">
                <div className="flex -space-x-1">
                  {topReactions.map(([type]) => (
                    <span key={type} className="w-5 h-5 flex items-center justify-center rounded-full bg-white dark:bg-gray-800 shadow-[0_0_0_2px_white] dark:shadow-[0_0_0_2px_#111827] text-[12px] leading-none">
                      {EMOJI_MAP[type] || '\uD83D\uDC4D'}
                    </span>
                  ))}
                </div>
                <span className="text-[12px] text-gray-500 dark:text-gray-400 font-medium">{totalReactions}</span>
              </div>
            ) : <div />}
            {repliesCount > 0 && (
              <button onClick={goToThread} className="text-[12px] text-gray-500 dark:text-gray-400 font-medium hover:text-indigo-500 dark:hover:text-indigo-400 transition-colors hover:underline">
                {repliesCount} {repliesCount === 1 ? 'commento' : 'commenti'}
              </button>
            )}
          </div>
        )}

        {/* Reaction bar inline */}
        <div className="px-4 py-1.5 border-t border-gray-100 dark:border-gray-800/60">
          <ReactionBar
            reactions={reactions}
            currentUserId={currentMemberId}
            onReact={handleReact}
            facebook
            extended
          />
        </div>

        {/* Inline replies */}
        {replies.length > 0 && (
          <div className="px-4 pt-1 pb-2 space-y-2 border-t border-gray-100 dark:border-gray-800/60">
            {repliesCount > replies.length && (
              <button onClick={goToThread} className="text-[12px] text-indigo-600 dark:text-indigo-400 font-medium hover:underline">
                Vedi tutti i {repliesCount} commenti
              </button>
            )}
            {replies.map(r => (
              <InlineReplyBubble key={r._id} reply={r} />
            ))}
          </div>
        )}

        {/* Comment input */}
        <div className="px-4 py-2.5 border-t border-gray-100 dark:border-gray-800/60 flex items-center gap-2">
          <input
            type="text"
            value={commentText}
            onChange={e => setCommentText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComment(); } }}
            placeholder="Scrivi un commento..."
            className="flex-1 text-[13px] bg-gray-100 dark:bg-gray-800 rounded-full px-4 py-2 text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 border-0 outline-none focus:ring-2 focus:ring-indigo-500/30"
          />
          <button
            onClick={handleComment}
            disabled={!commentText.trim() || submitting}
            className="p-2 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AreaPage;
